import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Lenis from "lenis";
import "@/index.css";
import { Layout, ScrollToTop } from "@/components/Layout";
import { CookieBanner } from "@/components/CookieBanner";
import { trackMetaPageView } from "@/lib/metaPixel";
import { trackPhoneClick, trackWhatsappClick, gtagPageConfig, hasTrackingConsent } from "@/lib/measurement";
import { trackPageview, trackEvent } from "@/lib/track";
import { captureAttribution, dl } from "@/lib/tracking";
import Home from "@/pages/Home";
// Route-Level Code-Splitting: Unterseiten laden erst beim Aufruf (PageSpeed: weniger ungenutztes JS)
const L = (loader, name) => lazy(() => loader().then((m) => ({ default: name ? m[name] : m.default })));
const ServicesIndex = L(() => import("@/pages/Services"), "ServicesIndex");
const ServicePage = L(() => import("@/pages/Services"), "ServicePage");
const Standorte = L(() => import("@/pages/Cities"), "Standorte");
const CityPage = L(() => import("@/pages/Cities"), "CityPage");
const KoelnLanding = L(() => import("@/pages/KoelnLanding"));
const KoelnBezirkPage = L(() => import("@/pages/KoelnBezirk"), "KoelnBezirkPage");
const KoelnEhrenfeldPage = L(() => import("@/pages/KoelnEhrenfeld"), "KoelnEhrenfeldPage");
const NewsletterBestaetigt = L(() => import("@/pages/NewsletterPages"), "NewsletterBestaetigt");
const NewsletterAbgemeldet = L(() => import("@/pages/NewsletterPages"), "NewsletterAbgemeldet");
const RatgeberArticle = L(() => import("@/pages/Ratgeber"), "RatgeberArticle");
const MagazinIndex = L(() => import("@/pages/Magazin"), "MagazinIndex");
const ArticlePage = L(() => import("@/pages/Magazin"), "ArticlePage");
const Admin = L(() => import("@/pages/Admin"));
const AuthorPage = L(() => import("@/pages/AuthorPage"));
const DistrictPage = L(() => import("@/pages/Districts"), "DistrictPage");
const FreundeEmpfehlen = L(() => import("@/pages/Marketing"), "FreundeEmpfehlen");
const Vertriebspartner = L(() => import("@/pages/Marketing"), "Vertriebspartner");
const Karriere = L(() => import("@/pages/Marketing"), "Karriere");
const BrandsIndex = L(() => import("@/pages/Brands"), "BrandsIndex");
const BrandPage = L(() => import("@/pages/Brands"), "BrandPage");
const Schadenbeispiele = L(() => import("@/pages/Brands"), "Schadenbeispiele");
const UnfallHub = L(() => import("@/pages/Unfall"), "UnfallHub");
const UnfallArtPage = L(() => import("@/pages/Unfall"), "UnfallArtPage");
const ParkplatzHub = L(() => import("@/pages/Unfall"), "ParkplatzHub");
const ParkplatzOrtPage = L(() => import("@/pages/Unfall"), "ParkplatzOrtPage");
const UnfallAssistent = L(() => import("@/pages/Unfall"), "UnfallAssistent");
const UnfallWasTun = L(() => import("@/pages/WasTun"));
const Ersteinschaetzung = L(() => import("@/pages/Ersteinschaetzung"));
const AnspruchPruefen = L(() => import("@/pages/AnspruchPruefen"));
const AdsLandingPage = L(() => import("@/pages/LandingPages"), "AdsLandingPage");
const RechtHub = L(() => import("@/pages/Wissen"), "RechtHub");
const UrteilPage = L(() => import("@/pages/Wissen"), "UrteilPage");
const FaqHub = L(() => import("@/pages/Wissen"), "FaqHub");
const FaqRoute = L(() => import("@/pages/Wissen"), "FaqRoute");
const GlossarHub = L(() => import("@/pages/Wissen"), "GlossarHub");
const GlossarBegriffPage = L(() => import("@/pages/Wissen"), "GlossarBegriffPage");
const RechnerHub = L(() => import("@/pages/Rechner"), "RechnerHub");
const RechnerPage = L(() => import("@/pages/Rechner"), "RechnerPage");
const OnlineKostenvoranschlag = L(() => import("@/pages/InfoPages"), "OnlineKostenvoranschlag");
const Downloads = L(() => import("@/pages/InfoPages"), "Downloads");
const Schadenarten = L(() => import("@/pages/InfoPages"), "Schadenarten");
const Qualitaet = L(() => import("@/pages/InfoPages"), "Qualitaet");
const Technik = L(() => import("@/pages/InfoPages"), "Technik");
const Team = L(() => import("@/pages/InfoPages"), "Team");
const UeberUns = L(() => import("@/pages/Company"), "UeberUns");
const Kontakt = L(() => import("@/pages/Company"), "Kontakt");
const Franchise = L(() => import("@/pages/Company"), "Franchise");
const SchadenMelden = L(() => import("@/pages/Forms"), "SchadenMelden");
const WerZahltGutachter = L(() => import("@/pages/SeoPages"), "WerZahltGutachter");
const FreieGutachterwahl = L(() => import("@/pages/SeoPages"), "FreieGutachterwahl");
const GutachterKosten = L(() => import("@/pages/SeoPages"), "GutachterKosten");
const Danke = L(() => import("@/pages/Forms"), "Danke");
const Impressum = L(() => import("@/pages/Legal"), "Impressum");
const Datenschutz = L(() => import("@/pages/Legal"), "Datenschutz");
const Cookies = L(() => import("@/pages/Legal"), "Cookies");
const NotFound = L(() => import("@/pages/NotFound"));
import { koelnBezirke, koelnBezirkPath } from "@/data/koelnBezirke";
import { unfallarten, unfallartPath, parkplatzOrte, parkplatzPath } from "@/data/unfaelle";
import { services } from "@/data/services";
import { ratgeber } from "@/data/ratgeber";
import { CITY_LINKS } from "@/data/site";
import { districts } from "@/data/districts";
import { brands } from "@/data/brands";

const TOP_LEVEL_ARTICLES = ratgeber.filter((r) => !r.path.startsWith("/ratgeber/"));

const MetaPixelTracker = () => {
  const location = useLocation();
  useEffect(() => {
    captureAttribution();
    gtagPageConfig(location.pathname);
    trackMetaPageView(`${location.pathname}${location.search}`);
    trackPageview(`${location.pathname}${location.search}`);
    const onConsent = () => trackMetaPageView(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("ue24-consent-changed", onConsent);
    return () => window.removeEventListener("ue24-consent-changed", onConsent);
  }, [location.pathname, location.search]);
  return null;
};

// Alte Autoren-URLs (/autor/<slug>) clientseitig auf /team/<slug> umleiten –
// serverseitig greift zusätzlich ein 301 (siehe backend REDIRECTS).
const TeamRedirect = () => <Navigate to={window.location.pathname.replace(/^\/autor\//, "/team/").replace(/\/$/, "")} replace />;

// Contact-Event für Meta Ads: jeder Klick auf tel:- oder WhatsApp-Links (global, gilt auf allen Seiten)
// + eigenes anonymes Tracking (Anruf/WhatsApp/Formular) für das Admin-Dashboard
const ContactClickTracker = () => {
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest?.("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
    if (href.startsWith("tel:")) {
        trackPhoneClick(href.replace("tel:", ""));
        if (hasTrackingConsent()) dl({ event: "phone_click", phone_number: href.replace("tel:", ""), page_path: window.location.pathname });
        trackEvent("anruf", a.getAttribute("data-testid") || a.textContent?.trim().slice(0, 60) || "tel-link");
      } else if (href.includes("wa.me") || href.includes("whatsapp.com")) {
        trackWhatsappClick();
        if (hasTrackingConsent()) dl({ event: "whatsapp_click", page_path: window.location.pathname });
        trackEvent("whatsapp", a.getAttribute("data-testid") || "wa-link");
      }
    };
    const onSubmit = (e) => {
      const f = e.target;
      trackEvent("formular", f?.getAttribute?.("data-testid") || f?.id || "form");
    };
    document.addEventListener("click", onClick);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);
  return null;
};

const AppShell = () => {
  useEffect(() => {
    // Smooth-Scrolling nur auf Desktop – auf dem Handy kostet Lenis Performance
    if (!window.matchMedia("(min-width: 768px)").matches) return undefined;
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.11 });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const location = useLocation();
  const isLp = location.pathname.startsWith("/lp/");

  const content = (
    <>
      <ScrollToTop />
      <CookieBanner />
      <MetaPixelTracker />
      <ContactClickTracker />
      <Toaster position="top-center" richColors />
      <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/lp/kfz-gutachter-koeln" element={<AdsLandingPage slug="kfz-gutachter-koeln" />} />
        <Route path="/lp/unfallgutachten" element={<AdsLandingPage slug="unfallgutachten" />} />
        <Route path="/lp/schaden-melden" element={<AdsLandingPage slug="schaden-melden" />} />
        <Route path="/lp/gutachter-in-der-naehe" element={<AdsLandingPage slug="gutachter-in-der-naehe" />} />
        <Route path="/anspruch-pruefen" element={<AnspruchPruefen />} />
        <Route path="/anspruch-pruefen/" element={<AnspruchPruefen />} />
        <Route path="/ueber-uns" element={<UeberUns />} />
        <Route path="/ueber-uns/" element={<UeberUns />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/standorte" element={<Standorte />} />
        <Route path="/kfz-sachverstaendiger-franchise" element={<Franchise />} />
        <Route path="/leistungen" element={<ServicesIndex />} />
        <Route path="/leistungen/" element={<ServicesIndex />} />
        <Route path="/schaden-melden-formular" element={<SchadenMelden variant="form" />} />
        <Route path="/schaden-melden" element={<SchadenMelden variant="content" />} />
        <Route path="/danke" element={<Danke />} />
        <Route path="/kfz-gutachter-koeln" element={<KoelnLanding />} />
        {/* Alte Stadtteil-URLs (koeln-<stadtteil>) auf die neuen Bezirksseiten umleiten */}
        {["innenstadt", "ehrenfeld", "nippes", "muelheim", "porz", "rodenkirchen", "lindenthal", "kalk", "chorweiler"].map((b) => (
          <Route key={b} path={`/kfz-gutachter-koeln-${b}`} element={<Navigate to={`/kfz-gutachter-koeln/${b}`} replace />} />
        ))}
        <Route path="/kfz-gutachter-koeln-deutz" element={<Navigate to="/kfz-gutachter-koeln/innenstadt" replace />} />
        <Route path="/kfz-gutachter-koeln/ehrenfeld" element={<KoelnEhrenfeldPage />} />
        {koelnBezirke.filter((b) => b.slug !== "ehrenfeld").map((b) => (
          <Route key={b.slug} path={koelnBezirkPath(b)} element={<KoelnBezirkPage slug={b.slug} />} />
        ))}
        {CITY_LINKS.filter((c) => c.slug !== "koeln").map((c) => (
          <Route key={c.slug} path={`/kfz-gutachter-${c.slug}`} element={<CityPage slug={c.slug} />} />
        ))}
        {districts.map((d) => (
          <Route key={d.slug} path={d.path} element={<DistrictPage slug={d.slug} />} />
        ))}
        <Route path="/freunde-empfehlen" element={<FreundeEmpfehlen />} />
        <Route path="/freunde-empfehlen/" element={<FreundeEmpfehlen />} />
        <Route path="/vertriebspartner" element={<Vertriebspartner />} />
        <Route path="/vertriebspartner/" element={<Vertriebspartner />} />
        <Route path="/karriere" element={<Karriere />} />
        <Route path="/karriere/" element={<Karriere />} />
        <Route path="/automarken" element={<BrandsIndex />} />
        <Route path="/automarken/" element={<BrandsIndex />} />
        <Route path="/schadenbeispiele" element={<Schadenbeispiele />} />
        <Route path="/schadenbeispiele/" element={<Schadenbeispiele />} />
        {brands.map((b) => (
          <Route key={b.slug} path={`/automarken/kfz-gutachten-${b.slug}`} element={<BrandPage slug={b.slug} />} />
        ))}
        <Route path="/unfall" element={<UnfallHub />} />
        <Route path="/unfall/" element={<UnfallHub />} />
        <Route path="/unfall-assistent" element={<UnfallAssistent />} />
        <Route path="/unfall-assistent/" element={<UnfallAssistent />} />
        <Route path="/unfall-was-tun" element={<UnfallWasTun />} />
        <Route path="/unfall-was-tun/" element={<UnfallWasTun />} />
        <Route path="/unfall-ersteinschaetzung" element={<Ersteinschaetzung />} />
        <Route path="/unfall-ersteinschaetzung/" element={<Ersteinschaetzung />} />
        <Route path="/unfall/parkplatz" element={<ParkplatzHub />} />
        <Route path="/unfall/parkplatz/" element={<ParkplatzHub />} />
        {parkplatzOrte.map((p) => (
          <Route key={p.slug} path={parkplatzPath(p)} element={<ParkplatzOrtPage slug={p.slug} />} />
        ))}
        {unfallarten.map((u) => (
          <Route key={u.slug} path={unfallartPath(u)} element={<UnfallArtPage slug={u.slug} />} />
        ))}
        <Route path="/kfz-gutachter-in-der-naehe" element={<CityPage slug="in-der-naehe" />} />
        <Route path="/ihr-recht" element={<RechtHub />} />
        <Route path="/ihr-recht/" element={<RechtHub />} />
        <Route path="/ihr-recht/:slug" element={<UrteilPage />} />
        <Route path="/ihr-recht/:slug/" element={<UrteilPage />} />
        <Route path="/faq" element={<FaqHub />} />
        <Route path="/faq/" element={<FaqHub />} />
        <Route path="/faq/:slug" element={<FaqRoute />} />
        <Route path="/faq/:slug/" element={<FaqRoute />} />
        <Route path="/glossar" element={<GlossarHub />} />
        <Route path="/glossar/" element={<GlossarHub />} />
        <Route path="/glossar/:slug" element={<GlossarBegriffPage />} />
        <Route path="/glossar/:slug/" element={<GlossarBegriffPage />} />
        <Route path="/rechner" element={<RechnerHub />} />
        <Route path="/rechner/" element={<RechnerHub />} />
        <Route path="/rechner/:slug" element={<RechnerPage />} />
        <Route path="/rechner/:slug/" element={<RechnerPage />} />
        <Route path="/online-kostenvoranschlag" element={<OnlineKostenvoranschlag />} />
        <Route path="/online-kostenvoranschlag/" element={<OnlineKostenvoranschlag />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route path="/downloads/" element={<Downloads />} />
        <Route path="/schadenarten" element={<Schadenarten />} />
        <Route path="/schadenarten/" element={<Schadenarten />} />
        <Route path="/qualitaet" element={<Qualitaet />} />
        <Route path="/qualitaet/" element={<Qualitaet />} />
        <Route path="/technik" element={<Technik />} />
        <Route path="/technik/" element={<Technik />} />
        <Route path="/team" element={<Team />} />
        <Route path="/team/" element={<Team />} />
        <Route path="/ratgeber" element={<MagazinIndex />} />
        <Route path="/ratgeber/" element={<MagazinIndex />} />
        <Route path="/ratgeber/page/:page" element={<MagazinIndex />} />
        <Route path="/ratgeber/page/:page/" element={<MagazinIndex />} />
        <Route path="/ratgeber/kategorie/:cat" element={<MagazinIndex />} />
        <Route path="/ratgeber/kategorie/:cat/" element={<MagazinIndex />} />
        <Route path="/ratgeber/vdi-5900-richtlinien-fuer-kfz-sachverstaendige" element={<RatgeberArticle slug="vdi-5900-richtlinien-fuer-kfz-sachverstaendige" />} />
        <Route path="/ratgeber/vdi-5900-richtlinien-fuer-kfz-sachverstaendige/" element={<RatgeberArticle slug="vdi-5900-richtlinien-fuer-kfz-sachverstaendige" />} />
        <Route path="/ratgeber/:slug" element={<ArticlePage />} />
        <Route path="/ratgeber/:slug/" element={<ArticlePage />} />
        <Route path="/wer-zahlt-den-kfz-gutachter" element={<WerZahltGutachter />} />
        <Route path="/freie-gutachterwahl" element={<FreieGutachterwahl />} />
        <Route path="/kfz-gutachter-kosten" element={<GutachterKosten />} />
        <Route path="/team/:slug" element={<AuthorPage />} />
        <Route path="/team/:slug/" element={<AuthorPage />} />
        <Route path="/autor/:slug" element={<TeamRedirect />} />
        <Route path="/autor/:slug/" element={<TeamRedirect />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/newsletter-bestaetigt" element={<NewsletterBestaetigt />} />
        <Route path="/newsletter-abgemeldet" element={<NewsletterAbgemeldet />} />
        <Route path="/danke/" element={<Danke />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutzerklaerung" element={<Datenschutz />} />
        <Route path="/cookies" element={<Cookies />} />
        {services.map((s) => (
          <Route key={s.slug} path={`/${s.slug}`} element={<ServicePage slug={s.slug} />} />
        ))}
        {TOP_LEVEL_ARTICLES.map((r) => (
          <Route key={r.slug} path={`/${r.slug}`} element={<RatgeberArticle slug={r.slug} />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );

  if (isLp) return content;
  return <Layout>{content}</Layout>;
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
